import React, { useCallback, useEffect, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { SearchKeywords } from "../MMHProductsServices";

/**
 * Keyword tagger for a single image.
 *
 * Suggestions come from the keyword master as you type (debounced), but the
 * field is a tag input, so an admin can also commit a brand-new keyword —
 * matching the legacy behaviour where typing an unknown keyword created it.
 *
 * Value is a plain array of keyword names. The API turns them into keyword
 * master ids for storage — the column the website searches on holds ids —
 * and hands names back when a product is loaded for editing.
 */
const KeywordSelect = ({ value = [], onChange, placeholder = "Type to search keywords" }) => {
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);

  const debounceRef = useRef(null);
  // Guards against a slow earlier request overwriting a newer one's results.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const handleSearch = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = query.trim();
    if (!term) {
      setOptions([]);
      setFetching(false);
      return;
    }

    setFetching(true);
    debounceRef.current = setTimeout(async () => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      try {
        const res = await SearchKeywords(term);
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        const list = res?.data?.data ?? res?.data ?? [];
        setOptions(Array.isArray(list) ? list : []);
      } catch (error) {
        if (mountedRef.current && requestId === requestIdRef.current) {
          setOptions([]);
        }
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) {
          setFetching(false);
        }
      }
    }, 350);
  }, []);

  return (
    <Select
      mode="tags"
      className="mmh-keyword-select"
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      onSearch={handleSearch}
      filterOption={false}
      tokenSeparators={[","]}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      maxTagCount="responsive"
allowClear
    >
      {options.map((keyword) => (
        <Select.Option key={keyword} value={keyword}>
          {keyword}
        </Select.Option>
      ))}
    </Select>
  );
};

export default KeywordSelect;
